import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { MediaData } from "./media";
import { OPMFDTValidationSchema } from "./rfs";

interface OPMFDTData {
  id: string;
  rfs: {
    id: string;
  };
  valid: boolean;
  revise: boolean | null;
  poleFdt: string;
  imageFdt: string;
  outputFdt: string;
  inputFdt: string;
  splitterFdt: string;
  poleFdtStatus: boolean;
  imageFdtStatus: boolean;
  outputFdtStatus: boolean;
  inputFdtStatus: boolean;
  splitterFdtStatus: boolean;
  poleFdtMedia: MediaData;
  imageFdtMedia: MediaData;
  outputFdtMedia: MediaData;
  inputFdtMedia: MediaData;
  splitterFdtMedia: MediaData;
  fdtIdText: string;
  outputFdtText: string;
  inputFdtText: string;
}

/* ============ *
 * READ OPM FDT *
 * ============ */

interface OPMFDTQueryKey {
  rfsId: string;
}
async function getOPMFDTs(
  queryFunctionContext: QueryFunctionContext,
): Promise<OPMFDTData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, opmFDTQueryKey] = queryKey;
  const { rfsId } = opmFDTQueryKey as OPMFDTQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/opm-fdt" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}
function useOPMFDTs(opmFDTQueryKey: OPMFDTQueryKey) {
  const { rfsId } = opmFDTQueryKey;
  return useQuery({
    queryKey: ["opmFdts", { rfsId }],
    queryFn: getOPMFDTs,
  });
}

interface Data {
  totalElements: number;
}

async function getFDTs(
  queryFunctionContext: QueryFunctionContext,
): Promise<Data> {
  const { queryKey } = queryFunctionContext;
  const [_, opmFDTQueryKey] = queryKey;
  const { rfsId } = opmFDTQueryKey as OPMFDTQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/opm-fdt" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data;
}
function useFDTs(opmFDTQueryKey: OPMFDTQueryKey) {
  const { rfsId } = opmFDTQueryKey;
  return useQuery({
    queryKey: ["opmFdts", { rfsId }],
    queryFn: getFDTs,
  });
}

interface GenerateResultPayload {
  imageFdtMedia: {
    path: string;
  };
  inputFdtMedia: { path: string };
  outputFdtMedia: { path: string };
  fdtId: string;
}

async function getDataFromImages(data: GenerateResultPayload): Promise<{
  fdtIdText: string;
  outputFdtText: string;
  inputFdtText: string;
}> {
  const response = await axiosInstance.post("/api/crud/opm-fdt/generate", data);
  return response.data.data;
}
function useOPMFDTGenerate() {
  return useMutation({
    mutationFn: (data: GenerateResultPayload) => getDataFromImages(data),
  });
}

async function validateOpmFdtInReview(
  id: string,
  data: OPMFDTValidationSchema,
) {
  const response = await axiosInstance.post(
    `/api/crud/opm-fdt/${id}/validate`,
    {
      id: data.opmFdt.id,
      inputFdtText: data.opmFdt.inputFdtText,
      outputFdtText: data.opmFdt.outputFdtText,
      fdtIdText: data.opmFdt.fdtIdText,
    },
  );
  return response.data;
}
function useInReviewValidateFDT() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OPMFDTValidationSchema }) =>
      validateOpmFdtInReview(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
      queryClient.invalidateQueries({
        queryKey: ["opmFdts", { rfsId: id }],
      });
    },
  });
}

export type { OPMFDTData };
export { useOPMFDTs, useFDTs, useOPMFDTGenerate, useInReviewValidateFDT };
